import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError, setCsrfToken } from '../lib/api';

export function Login() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('admin@example.com');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaUserId, setMfaUserId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api<{ user?: unknown; csrfToken?: string; mfa_required?: boolean; userId?: string }>(
        '/api/auth/login',
        { method: 'POST', body: JSON.stringify({ email, password }) },
      );
      if (r.mfa_required && r.userId) {
        setMfaUserId(r.userId);
        return;
      }
      if (r.csrfToken) setCsrfToken(r.csrfToken);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Unable to sign in');
    } finally { setBusy(false); }
  }

  async function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaUserId) return;
    setBusy(true); setErr(null);
    try {
      await api('/api/mfa/challenge', { method: 'POST', body: JSON.stringify({ userId: mfaUserId, code: mfaCode }) });
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Invalid code');
    } finally { setBusy(false); }
  }

  if (mfaUserId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="qa-eyebrow mb-3">Two-factor authentication</p>
            <h1 className="qa-h1">One last <span className="qa-italic-accent">code.</span></h1>
            <p className="qa-deck text-base mt-3 text-ink-mute">
              Enter the 6-digit code from your authenticator (or a 10-character backup code).
            </p>
          </div>
          <form onSubmit={onSubmitMfa} className="qa-card p-8 space-y-5">
            <div>
              <label htmlFor="mfa-code" className="qa-label">Code</label>
              <input id="mfa-code" className="qa-input qa-mono text-center text-lg tracking-widest" autoFocus
                value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\s/g, '').toLowerCase())}
                maxLength={10} />
            </div>
            {err && <div className="qa-badge qa-badge--out w-full justify-center">{err}</div>}
            <button type="submit" disabled={busy || mfaCode.length < 6} className="btn-primary w-full">
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" onClick={() => { setMfaUserId(null); setMfaCode(''); }} className="block text-center text-xs text-ink-mute hover:text-rust w-full">
              ← Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <p className="qa-eyebrow mb-3">Client—Base</p>
          <h1 className="qa-display text-5xl">
            Advisory work, <span className="qa-italic-accent">organised.</span>
          </h1>
          <p className="qa-deck text-lg mt-3 text-ink-mute">Sign in to the advisory client platform.</p>
        </div>
        <form onSubmit={onSubmit} className="qa-card p-8 space-y-5">
          <div>
            <label htmlFor="login-email" className="qa-label">Email</label>
            <input id="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="qa-input" autoComplete="email" />
          </div>
          <div>
            <label htmlFor="login-password" className="qa-label">Password</label>
            <input id="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="qa-input" autoComplete="current-password" />
          </div>
          {err && <div className="qa-badge qa-badge--out w-full justify-center">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full">{busy ? 'Signing in…' : 'Sign in'}</button>
          <div className="flex justify-between text-xs pt-2">
            <a href="/forgot-password" className="text-ink-mute hover:text-rust">Forgot password?</a>
            <a href="/request-access" className="text-ink-mute hover:text-rust">Request access</a>
          </div>
          <p className="text-xs text-ink-mute text-center pt-1">
            Synthetic dev data only. MFA required in production.
          </p>
        </form>
      </div>
    </div>
  );
}
